import {
  Box,
  convertToBlob,
  Dialog,
  downloadFile,
  unsafeLocalizedString,
  useAuthenticationContext,
  useDialogDimensionLimits,
} from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { RuntimeLocaleKey, useFormatMessage } from "../../../intl";
import { DocumentViewer } from "../../DocumentViewer/DocumentViewer";
import * as remoteData from "../../../RemoteData";
import * as api from "../../documentAPI";
import { constant, constNull, pipe } from "fp-ts/function";
import { useQuery } from "../../../useAPI";
import { spaceUnit } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { NonEmptyString } from "io-ts-types/NonEmptyString";
import { Option } from "fp-ts/Option";
import { getLatestToken } from "../../../APICall";
import { saveAs } from "file-saver";
import { DownloadIcon } from "@g4p/react-styleguide-library";

type ProductType = "SL" | "Mortgage";

export type DocumentViewDialogProps = {
  documentId: NonEmptyString;
  onClose: () => void;
  productType: ProductType;
};

function foldProduct<T>(
  productType: ProductType,
  match: { whenSL: () => T; whenMortgage: () => T }
) {
  switch (productType) {
    case "Mortgage":
      return match.whenMortgage();
    case "SL":
      return match.whenSL();
  }
}

export const DocumentViewDialog = (props: DocumentViewDialogProps) => {
  const { documentId, onClose } = props;
  const formatMessage = useFormatMessage();

  const { heightLimits } = useDialogDimensionLimits();

  const { maxHeight } = heightLimits();

  const { authInfo } = useAuthenticationContext();

  const [documentContent] = useQuery(
    foldProduct(props.productType, {
      whenSL: () => api.getDocumentContentSL,
      whenMortgage: () => api.getDocumentContentMortgage,
    }),
    {
      documentID: documentId,
    }
  );

  const getDownloadUrl = (
    downloadUrl: Option<NonEmptyString>,
    file: api.GetDocumentContentOutput
  ) =>
    pipe(
      downloadUrl,
      option.getOrElse(() =>
        URL.createObjectURL(convertToBlob(file.content, file.mimeType))
      )
    );

  const WrappedIcon = () => {
    return <DownloadIcon width={14} height={14} />;
  };

  return pipe(
    documentContent,
    remoteData.fold(constNull, constNull, docContent => (
      <Dialog
        ariaLabelMessageForCloseIcon={formatMessage(
          "Modal.closeButton" as RuntimeLocaleKey
        )}
        variant="left"
        size="medium"
        title={unsafeLocalizedString(docContent.filename)}
        actions={[
          {
            variant: "text",
            label: formatMessage("Download.buttonLabel"),
            icon: WrappedIcon,
            action() {
              foldProduct(props.productType, {
                whenSL: () =>
                  downloadFile(
                    getDownloadUrl(docContent.downloadUrl, docContent) +
                      pipe(
                        getLatestToken(authInfo),
                        option.map(token => "&authorization=" + token),
                        option.getOrElse(constant(""))
                      ),
                    docContent.filename
                  ),
                whenMortgage: () =>
                  saveAs(
                    convertToBlob(docContent.content, "PDF"),
                    docContent.filename
                  ),
              });
            },
          },
          {
            variant: "primary",
            label: formatMessage("Close"),
            action: onClose,
          },
        ]}
        onDismiss={option.some(onClose)}
      >
        <Box
          grow
          shrink
          style={{
            minHeight: maxHeight ? maxHeight - 60 * spaceUnit : "70vh",
          }}
        >
          <DocumentViewer
            name={docContent.filename}
            type={docContent.mimeType}
            data={docContent.content}
          />
        </Box>
      </Dialog>
    ))
  );
};
